import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import ModerationRuleModal from "./ModerationRuleModal";
import { reasonLabel, typeLabel, classifyProvenance } from "./rulePolicy";

const RULES_URL = "/admin/api/moderation_rules";

const ACTION_BADGE = {
  log_only: "bg-secondary",
  queue_for_review: "bg-warning text-dark",
  hide_for_review: "bg-danger",
};

const PROVENANCE_BADGE = {
  default: { cls: "bg-light text-muted border", label: "default" },
  edited: { cls: "bg-info text-dark", label: "edited" },
  custom: { cls: "bg-primary", label: "custom" },
};

function writeHeaders() {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  return { ...authenticatedHeadersHash(), "Content-Type": "application/json", "X-CSRF-Token": csrfToken };
}

// The admin rule editor: CRUD over Admin::ModerationRule (the reason -> {threshold,
// action} moderation dial). Reads the whole set from the controller index, writes
// through the action API, and refetches after each change (no read-back coupling).
function ModerationRulesPanel({ actionOptions, severeReasons, seededDefaults }) {
  const reasonKeys = globalThis.Qwoted?.REPORT_REASON_KEYS || [];
  const reasonLabels = globalThis.Qwoted?.REPORT_REASON_LABELS || {};
  const typeKeys = globalThis.Qwoted?.REPORT_REPORTABLE_TYPES || [];
  const typeLabels = globalThis.Qwoted?.REPORT_REPORTABLE_TYPE_LABELS || {};

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // undefined = modal closed; null = creating; an object = editing that rule.
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(() => {
    setLoading(true);
    fetch(RULES_URL, { headers: authenticatedHeadersHash() })
      .then((res) => {
        if (!res.ok) throw new Error("load failed");
        return res.json();
      })
      .then((data) => {
        setRules(data);
        setError(null);
      })
      .catch(() => setError("Could not load the rules."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const actionLabel = (value) => actionOptions.find((o) => o.value === value)?.label || value;

  function isSevereHide(rule) {
    return rule.action === "hide_for_review" && rule.reason && severeReasons.includes(rule.reason);
  }

  function severeWarning(rule, verb) {
    return `You are about to ${verb} the safety rule for ${reasonLabel(rule.reason, reasonLabels)} (a severe category). This reduces protection against that content.`;
  }

  // Send a write, surface a failure, and refresh — so a non-2xx or network error
  // never looks like a silent success (the row would otherwise re-render unchanged).
  async function writeAndReload(request, failureMessage) {
    try {
      const response = await request();
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError((data.errors || []).join(", ") || failureMessage);
        return;
      }
      setError(null);
    } catch {
      setError("Network error - please try again.");
      return;
    }
    load();
  }

  async function toggleEnabled(rule) {
    // eslint-disable-next-line no-alert
    if (rule.enabled && isSevereHide(rule) && !window.confirm(severeWarning(rule, "disable"))) return;
    await writeAndReload(
      () =>
        fetch(`${RULES_URL}/${rule.id}`, {
          method: "PATCH",
          headers: writeHeaders(),
          body: JSON.stringify({ moderation_rule: { enabled: !rule.enabled } }),
        }),
      "Could not update the rule.",
    );
  }

  async function deleteRule(rule) {
    const base = `Delete this rule? ${reasonLabel(rule.reason, reasonLabels)} on ${typeLabel(rule.reportable_type, typeLabels)} → ${actionLabel(rule.action)}.`;
    const message = isSevereHide(rule) ? `${severeWarning(rule, "delete")}\n\n${base}` : base;
    // eslint-disable-next-line no-alert
    if (!window.confirm(message)) return;
    await writeAndReload(
      () => fetch(`${RULES_URL}/${rule.id}`, { method: "DELETE", headers: writeHeaders() }),
      "Could not delete the rule.",
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          These rules decide what a report count does to feed content. Changes apply to new reports and are re-checked
          on the next reconcile; they do not un-hide already-hidden content.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setEditing(null)}>
          New rule
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Reason</th>
              <th>Content type</th>
              <th>Threshold</th>
              <th>Action</th>
              <th>Status</th>
              <th>Source</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan="7" className="text-muted text-center">
                  No rules. Add one to start moderating on reports.
                </td>
              </tr>
            )}
            {rules.map((rule) => {
              const provenance = PROVENANCE_BADGE[classifyProvenance(rule, seededDefaults)];
              return (
                <tr key={rule.id} className={rule.enabled ? "" : "text-muted"}>
                  <td>{reasonLabel(rule.reason, reasonLabels)}</td>
                  <td>{typeLabel(rule.reportable_type, typeLabels)}</td>
                  <td>{rule.threshold}</td>
                  <td>
                    <span className={`badge ${ACTION_BADGE[rule.action] || "bg-secondary"}`}>
                      {actionLabel(rule.action)}
                    </span>
                  </td>
                  <td>
                    <div className="form-check form-switch mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={rule.enabled}
                        aria-label={`Toggle rule ${rule.id}`}
                        onChange={() => toggleEnabled(rule)}
                      />
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${provenance.cls}`}>{provenance.label}</span>
                  </td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => setEditing(rule)}
                    >
                      Edit
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteRule(rule)}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing !== undefined && (
        <ModerationRuleModal
          rule={editing}
          allRules={rules}
          actionOptions={actionOptions}
          reasonKeys={reasonKeys}
          reasonLabels={reasonLabels}
          typeKeys={typeKeys}
          typeLabels={typeLabels}
          severeReasons={severeReasons}
          onSaved={() => {
            setEditing(undefined);
            load();
          }}
          onClose={() => setEditing(undefined)}
        />
      )}
    </div>
  );
}

ModerationRulesPanel.propTypes = {
  actionOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  severeReasons: PropTypes.arrayOf(PropTypes.string).isRequired,
  seededDefaults: PropTypes.arrayOf(
    PropTypes.shape({
      reason: PropTypes.string,
      reportable_type: PropTypes.string,
      action: PropTypes.string,
      threshold: PropTypes.number,
    }),
  ).isRequired,
};

export default ModerationRulesPanel;
